// Turns input.ts's Commands into `{seq, tick, cmd}` batches with redundancy against packet loss: every
// send re-includes the last few commands the server hasn't acked yet, not just the newest one. The
// server already dedups by seq (see match.ts's drainInbox) and reports the high-water mark back on every
// snapshot's `ackSeq` — this only ever forgets a command once that ack has passed its seq.
//
// Stage 3's real input buffer: `unacked` (uncapped — see MAX_RESEND's own comment on why that cap only
// ever applied to what goes back OUT over the wire) is what net/predictedGame.ts replays after every
// snapshot to reconstruct "what should my hero look like right now", keyed by the `tick` each command
// was tagged with at send time. `standingOrder` exists because that replay alone is not enough: a `move`
// or `attack` command has a LASTING effect in core (it sets a path/order a hero keeps acting on for many
// ticks after the command itself is acked and gone from `unacked`) — without remembering the latest one
// separately, prediction would show a hero stopping dead the instant their move order's ack arrived,
// even though the real hero just keeps walking. It is the latest ACKED one on purpose: the replay starts
// from a snapshot, and only an order the server had already applied by then belongs at the replay's
// start. An order still unacked is replayed from `unacked` at its own tick — replaying it from the
// snapshot instead applied a fresh click ~an RTT in the past, so the hero leapt that far on the first frame.
import type { ClientMessage, Command, TimedCommand } from '../../shared/protocol.js';

// Bounds the WIRE batch only. The one way a command is lost for good: every batch that carried it dropped,
// and MAX_RESEND newer ones issued before any ack — the server applies whatever seq is above its high-water
// mark (match.ts's drainInbox), so a later batch acks straight past it and ack() below forgets it unsent.
// That takes 8 commands inside one round trip plus a resend interval with the throttled aim (remoteGame.
// ts's AIM_INTERVAL) counted in — rarer than the rate limit a bigger window would hit instead: the server
// charges each batch by its command count (config.ts's commandsPerSec 20), and every resend repeats the
// whole window.
const MAX_RESEND = 8;
const MAX_UNACKED = 512;  // a safety bound on retained history, not a tuned value — see the module header

/** Sets a lasting order a hero keeps acting on after the command itself is applied (a hero walks a path
    for many ticks off one `move`); `stop` explicitly cancels it. Everything else (`cast`, `release`,
    `aim`, the base-shop commands) is one-shot: its effect is fully realized the tick it applies, so it
    only needs replaying while still actually unacked. */
function standingOrderOf(cmd: Command): Command | null | undefined {
  if (cmd.type === 'move' || cmd.type === 'attack' || cmd.type === 'follow') return cmd;
  if (cmd.type === 'stop') return null;
  return undefined;  // no change
}

export type CommandChannel = {
  /** Queues `cmd` and sends it (with whatever's still unacked) immediately — one-shot orders (move,
      attack, cast) are latency-sensitive; batching them behind a timer would only add lag for no gain. */
  send(cmd: Command): void;
  /** Drops every command at or before `ackSeq` from the resend buffer — call this with each snapshot's
      own `ackSeq` as it arrives. */
  ack(ackSeq: number): void;
  /** Resends whatever is still unacked, without adding a new command — a periodic call (independent of
      new input) is what makes a lost packet invisible instead of a dropped order. */
  resend(): void;
  /** Where a resuming connection's own seq counter picks up — see protocol.ts's RoomState.yourAckSeq. */
  resumeFrom(ackSeq: number): void;
  /** Shifts every unacked command's `tick` (not the acked standing order — see its implementation) by `deltaTicks` — for when
      net/clockSync.ts re-bases the client's own tick counter by more than a rounding error (a tab stall,
      a big jitter correction) after commands were already tagged against the OLD numbering. Without this,
      predictedGame.ts's `tick <= replayTick` comparisons would misalign against the new numbering and
      either replay commands too early or drop them from a window that already passed. */
  rebaseTicks(deltaTicks: number): void;
  /** Every command not yet acked, oldest first — the actual input buffer stage 3's prediction replays.
      Not capped by MAX_RESEND: that cap bounds what goes back out over the wire, not what is kept for
      local replay, where losing an old-but-still-unacked entry would silently corrupt the reconstruction,
      not just shrink the resend window. */
  readonly unacked: readonly TimedCommand[];
  /** The most recent ACKED move/attack/follow command (or null after an acked `stop`) — see the module
      header on why replaying `unacked` alone is not enough to predict an ongoing walk, and why acked. */
  readonly standingOrder: TimedCommand | null;
};

export function createCommandChannel(sendMessage: (msg: ClientMessage) => void, clientTick: () => number): CommandChannel {
  let seq = 0;
  const unacked: TimedCommand[] = [];
  let standingOrder: TimedCommand | null = null;

  function flush() {
    // The wire tick is clamped at 0: the client clock runs negative through the pre-match countdown
    // (remoteGame.ts's startCountdown) and may still be there for an order given right as play starts
    // (no pong yet, a hidden tab), but the server's validator only takes ticks >= 0 and would drop it.
    // Local ticks stay unclamped — prediction replays against the client's own numbering.
    if (unacked.length) sendMessage({ type: 'cmd', commands: unacked.slice(-MAX_RESEND).map(c => (c.tick < 0 ? { ...c, tick: 0 } : c)) });
  }
  function promote(acked: readonly TimedCommand[]) {
    for (const c of acked) {
      const standing = standingOrderOf(c.cmd);
      if (standing !== undefined) standingOrder = standing ? c : null;
    }
  }
  function ack(ackSeq: number) {
    let n = 0;
    while (n < unacked.length && unacked[n]!.seq <= ackSeq) n++;
    promote(unacked.splice(0, n));
  }

  return {
    send(cmd) {
      seq++;
      const timed: TimedCommand = { seq, tick: clientTick(), cmd };
      unacked.push(timed);
      if (unacked.length > MAX_UNACKED) unacked.splice(0, unacked.length - MAX_UNACKED);
      flush();
    },
    ack,
    resend: flush,
    // Clears the resend buffer, not just the seq counter: a pre-disconnect order still sitting in
    // `unacked` carries an OLD seq that is now <= the new counter's starting point, so it would win a
    // duplicate-seq race against the next real command and get silently dropped as stale (match.ts's
    // dedup keys purely on seq, oldest-wins-nothing, newest-applies). Inert while only a fresh reconnect
    // (an empty channel) ever calls this — real on a live one, which stage 3 does by design. Whatever the
    // server reports as applied is acked first, so a standing order survives a resume (a walk in progress
    // does not stop because the socket blipped).
    resumeFrom(ackSeq) {
      ack(ackSeq);
      seq = ackSeq;
      unacked.length = 0;
    },
    // Only the unacked tail: the standing order is already acked, so its tick is history the server has
    // lived through, and predictedGame.ts compares it against snapshot ticks (a death, a finished walk) —
    // shifting it forward after a long stall would revive an order a death had ended.
    rebaseTicks(deltaTicks) {
      for (const c of unacked) (c as { tick: number }).tick += deltaTicks;
    },
    get unacked() { return unacked; },
    get standingOrder() { return standingOrder; },
  };
}
